import type { ReactNode } from 'react';

import { AppIcons } from '@web/ui/core/atoms';
import { Button } from '@web/ui/core/atoms/button';
import { Card } from '@web/ui/core/atoms/card';

interface DangerZoneCardProps {
  label: string;
  title: string;
  sub: ReactNode;
  buttonLabel: string;
  disabled?: boolean;
  onAction?: () => void;
}

/**
 * Danger-zone card — red-tinted Card with eyebrow header, copy block
 * and a trailing danger button. Used by settings pages whose only
 * destructive action is a project/squad delete.
 */
export function DangerZoneCard({
  label,
  title,
  sub,
  buttonLabel,
  disabled,
  onAction,
}: Readonly<DangerZoneCardProps>): React.JSX.Element {
  return (
    <Card tone="red">
      <div className="overflow-hidden rounded-lg">
        <header className="flex items-center gap-1.5 border-b border-tone-red-fg/22 bg-tone-red-tint/55 px-3.5 py-2 font-mono text-tiny font-medium uppercase tracking-eyebrow-loose text-tone-red-ink">
          <AppIcons name="warning" size={12} />
          <span>{label}</span>
        </header>
        <div className="flex items-center justify-between gap-4 p-4">
          <div className="min-w-0">
            <div className="text-card-meta font-medium text-foreground">{title}</div>
            <div className="mt-0.5 text-meta leading-normal text-muted-foreground">{sub}</div>
          </div>
          <Button variant="danger" disabled={disabled} onClick={onAction}>
            <AppIcons name="warning-octagon" size={13} />
            <span>{buttonLabel}</span>
          </Button>
        </div>
      </div>
    </Card>
  );
}
